import { useState } from "react";
import { Phone, MessageCircle, Globe, QrCode, User } from "lucide-react";

/**
 * Real-size digital business card driven by live card data.
 * Front = identity, Back = QR of the public URL. Flips on click.
 */
export default function CardPreview({ card, publicUrl }) {
  const [flipped, setFlipped] = useState(false);
  const colors = card.colors || {};
  const primary = colors.primary || "#E25A38";
  const bg = colors.background || "#1C1917";
  const isDark = isColorDark(bg);
  const textMain = isDark ? "#FFFFFF" : "#1C1917";
  const textSub = isDark ? "rgba(255,255,255,0.6)" : "rgba(28,25,23,0.6)";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(
    publicUrl || "https://xuma.mx"
  )}`;

  return (
    <div className="perspective w-full max-w-[420px] aspect-[1.58/1] mx-auto select-none">
      <button
        type="button"
        onClick={() => setFlipped((f) => !f)}
        data-testid="flip-card-container"
        aria-label="Girar tarjeta"
        className={`flip-inner block w-full h-full text-left cursor-pointer rounded-2xl shadow-[0_30px_60px_-20px_rgba(28,25,23,0.45)] ${
          flipped ? "flipped" : ""
        }`}
      >
        {/* FRONT */}
        <div className="flip-face flip-front rounded-2xl p-7 flex flex-col justify-between" style={{ background: bg }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] font-medium" style={{ color: primary }}>
                {card.organization || "XUMA"}
              </div>
              <h3 className="text-2xl font-heading font-extrabold mt-3 leading-none truncate" style={{ color: textMain }}>
                {card.name || "Tu Nombre"}
              </h3>
              <p className="text-sm mt-1 truncate" style={{ color: textSub }}>
                {card.profession || "Tu profesión o negocio"}
              </p>
            </div>
            {card.avatar ? (
              <img src={card.avatar} alt="" className="h-14 w-14 rounded-full object-cover shrink-0 border-2" style={{ borderColor: primary }} />
            ) : (
              <span className="h-12 w-12 rounded-xl grid place-items-center shrink-0" style={{ background: primary }}>
                <User className="h-5 w-5 text-white" />
              </span>
            )}
          </div>
          <div>
            {card.slogan && (
              <p className="text-sm italic mb-3 line-clamp-1" style={{ color: isDark ? "rgba(255,255,255,0.9)" : "#1C1917" }}>
                "{card.slogan}"
              </p>
            )}
            <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: textSub }}>
              {card.phone && (
                <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {card.phone}</span>
              )}
              {card.whatsapp && (
                <span className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" style={{ color: primary }} /> WhatsApp</span>
              )}
            </div>
          </div>
          <span className="absolute bottom-3 right-4 text-[10px]" style={{ color: textSub }}>Toca para girar</span>
        </div>

        {/* BACK */}
        <div className="flip-face flip-back rounded-2xl p-7 flex items-center gap-6 bg-white" style={{ borderTop: `4px solid ${primary}` }}>
          <img src={qrUrl} alt="Código QR" className="h-32 w-32 rounded-lg shrink-0" loading="lazy" />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-xuma-muted">Escanea</p>
            <h4 className="font-heading font-bold text-xuma-dark text-lg leading-tight mt-1">Comparte mi tarjeta</h4>
            {card.website && (
              <p className="text-sm text-xuma-muted mt-2 flex items-center gap-1.5 truncate">
                <Globe className="h-4 w-4 shrink-0" style={{ color: primary }} /> {card.website.replace(/^https?:\/\//, "")}
              </p>
            )}
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-xuma-muted">
              <QrCode className="h-3.5 w-3.5" /> XUMA
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

function isColorDark(hex) {
  if (!hex || hex[0] !== "#") return true;
  const c = hex.substring(1);
  const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  const r = parseInt(full.substr(0, 2), 16);
  const g = parseInt(full.substr(2, 2), 16);
  const b = parseInt(full.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}
